import React from 'react';
import { RunScenario } from '../../../services/benchmarkService';
import { scenarioName } from '../BenchmarkVocabulary';

/** The run's scenarios, as its manifest recorded them; null on runs scored before mappings. */
export type RunScenarios = Record<string, RunScenario> | null;

/**
 * Provided by InvestigationBenchmarkView, so the Cases table deep inside a dimension
 * reads the run's scenarios without threading them through every panel in between.
 */
export const RunScenariosContext = React.createContext<RunScenarios>(null);

export interface AlertTypologies {
    /** Every typology the case's rules are approved as detecting, in rule order, deduplicated. */
    typologies: { code: string; label: string }[];
    /** Each rule, named, and whether it was mapped. */
    scenarios: { code: string; name: string; mapped: boolean }[];
}

/**
 * What a case's alerts claimed, as typologies: the union over every rule that alerted on
 * it. A rule with no approved mapping is kept in `scenarios` as unmapped rather than
 * dropped, so a case raised only by unmapped rules reads "Not mapped", never blank.
 */
export function alertTypologies(scenarioIds: string[], catalog: RunScenarios): AlertTypologies {
    const typologies = new Map<string, string>();
    const scenarios = scenarioIds.map((code) => {
        const entry = catalog?.[code];
        for (const t of entry?.typologies ?? []) if (!typologies.has(t.code)) typologies.set(t.code, t.label);
        return { code, name: entry?.label || scenarioName(code), mapped: Boolean(entry?.typologies?.length) };
    });
    return { typologies: [...typologies].map(([code, label]) => ({ code, label })), scenarios };
}

/** "Raised by STRUCT_01 — Cash structuring below CTR (not mapped)", one line per rule. */
export const raisedByLine = (result: AlertTypologies): string =>
    result.scenarios.map((s) => `${s.code}${s.name && s.name !== s.code ? ` — ${s.name}` : ''}${s.mapped ? '' : ' (not mapped)'}`)
        .join('; ');
